import { useState, useEffect } from 'react';
import { AuthService } from '../services/authService';
import { User, AuthState, LoginCredentials, RegisterCredentials } from '../types/user.types';

export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let mounted = true;
    let debounceTimer: NodeJS.Timeout | null = null;
    let lastStableUser: User | null = null;

    // SOLO usar onAuthStateChange como fuente única de verdad con debounce
    const { data: authListener } = AuthService.onAuthStateChange((user) => {
      if (!mounted) return;

      // Limpiar timer anterior
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Debounce de 100ms para ignorar oscilaciones rápidas
      debounceTimer = setTimeout(() => {
        if (!mounted) return;

        const userId = user?.id;
        const lastUserId = lastStableUser?.id;

        // Solo actualizar si el usuario realmente cambió
        if (userId !== lastUserId) {
          lastStableUser = user;
          setState({ user, loading: false, error: null });
        }
      }, 100);
    });

    return () => {
      mounted = false;
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      authListener.subscription.unsubscribe();
    };
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const { user } = await AuthService.login(credentials);
      setState({ user: user as User, loading: false, error: null });
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: (error as Error).message,
      }));
      throw error;
    }
  };

  const register = async (credentials: RegisterCredentials) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const { user } = await AuthService.register(credentials);
      setState({ user: user as User, loading: false, error: null });
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: (error as Error).message,
      }));
      throw error;
    }
  };

  const logout = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      await AuthService.logout();
      setState({ user: null, loading: false, error: null });
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: (error as Error).message,
      }));
    }
  };

  return {
    user: state.user,
    loading: state.loading,
    error: state.error,
    login,
    register,
    logout,
    isAuthenticated: !!state.user,
  };
};
